import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AuthError = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    // Redirect to login page after logout
    navigate('/login');
  };

  // Check if this looks like a deleted/deactivated user scenario
  const isLikelyDeletedUser = user && !user.role && (!user.roles || user.roles.length === 0);

  return (
    <div className="max-w-md w-full space-y-8">
      <div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {isLikelyDeletedUser ? 'Account Access Denied' : 'Authentication Error'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {isLikelyDeletedUser 
            ? 'Your account appears to have been deactivated or removed'
            : 'There was a problem with your account access'
          }
        </p>
      </div>

      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              {isLikelyDeletedUser ? 'Account Deactivated' : 'Account Access Issue'}
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {isLikelyDeletedUser ? (
                <div>
                  <p>Your account has been deactivated or removed from the system.</p>
                  <p className="mt-2">This typically happens when:</p>
                  <ul className="mt-1 list-disc list-inside">
                    <li>An administrator has deactivated your account</li>
                    <li>Your company's access has been suspended</li>
                    <li>Your account was deleted due to inactivity</li>
                  </ul>
                </div>
              ) : (
                <div>
                  <p>Your account appears to have access issues. This could be due to:</p>
                  <ul className="mt-2 list-disc list-inside">
                    <li>Your account has been deactivated or deleted</li>
                    <li>Your role or permissions have been removed</li>
                    <li>Your account setup is incomplete</li>
                    <li>There was a system error with your account</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {user && (
        <div className="rounded-md bg-blue-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Account Information
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Name:</strong> {user.name}</p>
                <p><strong>User Type:</strong> {user.user_type}</p>
                <p><strong>Role:</strong> {user.role || 'Not assigned'}</p>
                {user.company && (
                  <p><strong>Company:</strong> {user.company.name}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="text-center">
          <h4 className="text-sm font-medium text-gray-900 mb-2">What to do next:</h4>
          <div className="space-y-2 text-sm text-gray-600">
            {isLikelyDeletedUser ? (
              <>
                <p>1. Contact your system administrator to reactivate your account</p>
                <p>2. Verify your company's account status</p>
                <p>3. If you believe this is an error, contact support immediately</p>
              </>
            ) : (
              <>
                <p>1. Contact your system administrator</p>
                <p>2. Or try logging out and back in</p>
                <p>3. If the problem persists, contact support</p>
              </>
            )}
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleLogout}
            className="flex-1 bg-red-600 px-4 py-2 rounded-md text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Log Out
          </button>
          <Link
            to="/login"
            className="flex-1 bg-gray-100 px-4 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 text-center"
          >
            Try Again
          </Link>
        </div>
      </div>

      <div className="text-center">
        <div className="text-xs text-gray-500">
          <p>Need help? Contact support at:</p>
          <p className="font-medium">support@3plwms.com</p>
        </div>
      </div>
    </div>
  );
};

export default AuthError; 